import { type Ref, useCallback, useLayoutEffect, useRef } from "react";
import { assignRef } from "./refs";

/**
 * Headless UI names its controls only from its own `<Label>` and drops a caller's
 * `aria-labelledby`, so an outside label (a form field's) is applied to the element after each
 * render. Returns a ref for the element that also forwards to `ref`.
 */
export const useLabelledBy = <T extends Element>(labelledBy: string | undefined, ref?: Ref<T>) => {
	const element = useRef<T | null>(null);

	useLayoutEffect(() => {
		if (labelledBy !== undefined) element.current?.setAttribute("aria-labelledby", labelledBy);
	});

	return useCallback(
		(node: T | null) => {
			element.current = node;
			assignRef(ref, node);
		},
		[ref],
	);
};
